import type { StructuredDiffPayload } from "~/components/StructuredDiffViewer/StructuredDiffViewer";
import type { AIChatApi } from "../../../functions/api/aiChat";
import type { ChatMessage as ChatMessageType, GenerationMode } from "../types";

/**
 * Mastra v1 (MastraMessageContentV2) 対応:
 * content が { format: 2, parts: [{ type: "text", text: "..." }] } の場合にテキストを抽出
 */
// biome-ignore lint/suspicious/noExplicitAny: Mastra v1のcontent形式が複数パターンあるため動的型チェックが必要
const extractRawText = (content: any): string => {
  if (typeof content === "string") return content;
  if (content && typeof content === "object") {
    if (content.format === 2 && Array.isArray(content.parts)) {
      // biome-ignore lint/suspicious/noExplicitAny: parts配列の要素型が不定
      const textPart = content.parts.find((p: any) => p.type === "text");
      if (textPart?.text) return String(textPart.text);
    }
    if (typeof content.content === "string") return content.content;
  }
  return String(content ?? "");
};

/**
 * Mastra v1ではmetadataが content.metadata に格納されるケースがあるため、
 * トップレベルと content 内の両方をフォールバック検索する
 */
// biome-ignore lint/suspicious/noExplicitAny: Mastraメッセージの型が不定
const extractMetadata = (m: any): Record<string, any> | undefined => {
  if (m.metadata && typeof m.metadata === "object") return m.metadata;
  if (m.content && typeof m.content === "object" && m.content.metadata) return m.content.metadata;
  return undefined;
};

/**
 * スレッドのメッセージを読み込んで正規化する
 * @param thread スレッドオブジェクト
 * @param generationMode 生成モード
 * @param api AIChatApiインスタンス
 * @returns 正規化されたメッセージ配列
 */
export const loadMessages = async (
  thread: any,
  generationMode: GenerationMode,
  api: AIChatApi,
): Promise<ChatMessageType[]> => {
  const agentId = "commonAgent";
  const tid = thread?.id || thread?.threadId;
  if (!tid) return [];

  const raw = await api.getMessages(agentId, tid, 30);
  const arr = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.messages)
      ? (raw as any).messages
      : [];
  const sorted = [...arr].sort(
    (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime(),
  );

  const result: ChatMessageType[] = [];
  let currentAi: ChatMessageType | null = null;

  const ensureCurrentAi = (baseTs: string | number | Date): ChatMessageType => {
    if (!currentAi) {
      currentAi = {
        id: `assistant_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        content: "",
        sender: "assistant",
        timestamp: new Date(baseTs || Date.now()),
        generationMode: generationMode,
        toolCalls: [],
      } as ChatMessageType;
    }
    return currentAi as ChatMessageType;
  };

  for (const m of sorted) {
    const role = String(m.role || "").toLowerCase();
    const ts = new Date(m.createdAt || Date.now());

    // ユーザーメッセージの処理
    if (role === "user") {
      let text = extractRawText(m.content);
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object" && parsed.message) {
          text = String(parsed.message);
        }
      } catch {
        // JSON解析に失敗した場合は元の文字列を使用
      }

      result.push({
        id: m.id || `user_${ts.getTime()}`,
        content: text,
        sender: "user",
        timestamp: ts,
        generationMode: "agent",
      });
      currentAi = null;
      continue;
    }

    if (role === "assistant") {
      // システムメッセージの判定（v1ではmetadataがcontent内に格納される場合がある）
      const meta = extractMetadata(m);
      const isSystemMessage = meta?.["messageType"] === "system";

      if (isSystemMessage) {
        // ナレッジメッセージとして処理
        const text = extractRawText(m.content);

        result.push({
          id: m.id || `system_${ts.getTime()}`,
          content: text,
          sender: "system",
          timestamp: ts,
          generationMode: "agent",
          metadata: {
            knowledgeId: meta?.["knowledgeId"],
          },
        });
        currentAi = null;
        continue;
      }

      // 通常のアシスタントメッセージの処理
      const ai = ensureCurrentAi(ts);
      const text = extractRawText(m.content);

      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object") {
          if (parsed.message) ai.content = String(parsed.message);
          if (parsed.updatedContent) {
            ai.updatedContent = String(parsed.updatedContent);
          }
          if (parsed.structuredDiff) {
            ai.structuredDiff = parsed.structuredDiff as StructuredDiffPayload;
          }
          if (Array.isArray(parsed.sourceUrls)) {
            ai.sourceUrls = parsed.sourceUrls as Array<{ title: string; url: string }>;
          }
          if (parsed.taskPlanning)
            ai.structuredData = { ...(ai.structuredData || {}), taskPlanning: parsed.taskPlanning };
        } else {
          ai.content = text;
        }
      } catch {
        ai.content = text;
      }

      // metadataからもupdatedContentを確認（v1ではcontent.metadata内の場合もある）
      if (meta?.["updatedContent"]) {
        ai.updatedContent = String(meta["updatedContent"]);
      } else if (meta?.["structuredOutput"]?.["updatedContent"]) {
        ai.updatedContent = String(meta["structuredOutput"]["updatedContent"]);
      }
      if (meta?.["structuredDiff"]) {
        ai.structuredDiff = meta["structuredDiff"] as StructuredDiffPayload;
      } else if (meta?.["structuredOutput"]?.["structuredDiff"]) {
        ai.structuredDiff = meta["structuredOutput"]["structuredDiff"] as StructuredDiffPayload;
      }

      // parts配列からもupdatedContentを確認（v1ではcontent.parts）
      const parts = m.content?.parts ?? m.parts;
      if (Array.isArray(parts)) {
        for (const part of parts) {
          if (part.type === "text" && part.text && part.text.includes("updatedContent")) {
            try {
              const partParsed = JSON.parse(part.text);
              if (partParsed.updatedContent) {
                ai.updatedContent = String(partParsed.updatedContent);
              }
              if (partParsed.structuredDiff) {
                ai.structuredDiff = partParsed.structuredDiff as StructuredDiffPayload;
              }
            } catch {
              // JSON parse error - skip
            }
          }
        }
      }

      // 完結
      result.push(ai);
      currentAi = null;
    }
  }

  // ストリーム途中で終わっていた場合のフォールバック
  const aiRef = currentAi as ChatMessageType | null;
  if (aiRef && (aiRef.content || aiRef.toolCalls?.length)) {
    result.push(aiRef);
  }

  return result;
};

/**
 * ID生成関数
 * @param prefix IDのプレフィックス
 * @returns 生成されたID
 */
export const generateMessageId = (prefix: string): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
